import {
  ButtonIcon,
  Flex,
  IconInformationAlt,
  ListBox,
  ListBoxRow,
  Pressable,
  Text,
} from '@spotify-internal/encore-web';
import { Popover, PopoverRoot } from '@spotify-internal/encore-web/next';
import { NumberUtils } from '@spotify/confidence-core-react';
import {
  type ObservationsV1IssueAssessment,
  ObservationsV1IssuePriorityLevel,
} from '@spotify/confidence-graphql';

import { ISSUE_PRIORITY_OPTIONS, getIssuePriorityLabel } from '../../types';
import { IssuePriorityColoredIcon } from './IssuePriorityColoredIcon';
import { IssuePriorityIcon } from './IssuePriorityIcon';

import styles from './IssuePriorityPicker.module.css';

export type IssuePriorityPickerProps = {
  assessment?: ObservationsV1IssueAssessment | null;
  disabled?: boolean;
  disableIcon?: boolean;
  justification?: string | null;
  value?: ObservationsV1IssuePriorityLevel | null;
  onChange: (priority: ObservationsV1IssuePriorityLevel) => void;
};

const IssuePriorityPickerOptions = ({
  close,
  value,
  onChange,
}: Pick<IssuePriorityPickerProps, 'value' | 'onChange'> & {
  close: () => void;
}) => {
  const handleSelectionChange = (keys: 'all' | Set<React.Key>): void => {
    if (keys === 'all') {
      return;
    }

    const [selectedKey] = keys;
    const selectedOption = ISSUE_PRIORITY_OPTIONS.find(
      option => option.value === selectedKey,
    );

    if (!selectedOption) {
      return;
    }

    onChange(selectedOption.value);
    close();
  };

  return (
    <ListBox
      aria-label="Change issue priority"
      selectionMode="single"
      selectedKeys={value ? new Set([value]) : new Set()}
      onSelectionChange={handleSelectionChange}
    >
      {ISSUE_PRIORITY_OPTIONS.map(option => (
        <ListBoxRow
          key={option.value}
          id={option.value}
          textValue={option.label}
          size="small"
          isDisabled={option.value === value}
        >
          <span className={styles.optionContent}>
            <IssuePriorityIcon priority={option.value} />
            <Text as="span" variant="bodySmall">
              {option.label}
            </Text>
          </span>
        </ListBoxRow>
      ))}
    </ListBox>
  );
};

const formatPriorityScore = (score?: number | null): string =>
  score === undefined || score === null
    ? 'Not available'
    : NumberUtils.formatNumber(score);

const IssuePriorityDetails = ({
  assessment,
  justification,
}: Pick<IssuePriorityPickerProps, 'assessment' | 'justification'>) => (
  <Flex direction="column" gap="tighter-2" className={styles.details}>
    <Text as="p" variant="bodySmallBold">
      Priority details
    </Text>
    <Flex gap="base" wrap="wrap">
      {[
        { label: 'Reach', value: assessment?.reach },
        { label: 'Impact', value: assessment?.impact },
        { label: 'Evidence', value: assessment?.evidence },
      ].map(score => (
        <Flex key={score.label} direction="column" gap="tighter-4">
          <Text as="span" variant="bodySmall" semanticColor="textSubdued">
            {score.label}
          </Text>
          <Text as="span" variant="bodySmallBold">
            {formatPriorityScore(score.value)}
          </Text>
        </Flex>
      ))}
    </Flex>
    <Flex direction="column" gap="tighter-4">
      <Text as="span" variant="bodySmall" semanticColor="textSubdued">
        Justification
      </Text>
      <Text as="span" variant="bodySmall">
        {justification?.trim() || 'Not available'}
      </Text>
    </Flex>
  </Flex>
);

export const IssuePriorityPicker = ({
  assessment,
  disabled = false,
  disableIcon = false,
  justification,
  value,
  onChange,
}: IssuePriorityPickerProps) => {
  const label = value ? getIssuePriorityLabel(value) : 'No priority';
  const priority = (
    <>
      {!disableIcon && <IssuePriorityColoredIcon priority={value} />}
      <Text as="span" variant="bodySmall">
        {label}
      </Text>
    </>
  );

  return (
    <span className={styles.picker}>
      {disabled ? (
        <span className={styles.trigger}>{priority}</span>
      ) : (
        <PopoverRoot>
          <Pressable>
            <button
              type="button"
              className={styles.trigger}
              data-editable
              aria-label={`Change issue priority, current value: ${label}`}
             data-track-source-anchor="cd950f15cb;issue-priority-picker-button">
              {priority}
            </button>
          </Pressable>
          <Popover
            aria-label="Change issue priority"
            placement="bottomStart"
            hasNoArrow
            className={styles.popover}
          >
            {({ close }) => (
              <IssuePriorityPickerOptions
                value={value}
                onChange={onChange}
                close={close}
               data-track-source-anchor="7d167f3834;issue-priority-picker-issue-priority-picker"/>
            )}
          </Popover>
        </PopoverRoot>
      )}
      <PopoverRoot>
        <ButtonIcon
          variant="tertiary"
          size="small"
          aria-label="Show priority details"
          onClick={event => event.stopPropagation()}
         data-track-source-anchor="dec9a76820;;low">
          <IconInformationAlt iconSize={16} aria-hidden />
        </ButtonIcon>
        <Popover
          aria-label="Priority details"
          placement="top"
          className={styles.detailsPopover}
        >
          <IssuePriorityDetails
            assessment={assessment}
            justification={justification}
          />
        </Popover>
      </PopoverRoot>
    </span>
  );
};
